import {
  Collaboration,
  CollabParticipants,
  EmployeeStatus,
  EmployeeType,
  Message,
  Patient,
  PatientDetails,
  Provider,
} from '@ffd/api-client';
import { CollaborationMessage, CollabSidebarElement, MessageVM, SelectedParticipant } from '@libs/collaboration';
import { Person } from '@libs/ui-lib';
import { filterWithRemaining, UNKNOWN_PROVIDER_FIRST_NAME, UNKNOWN_PROVIDER_LAST_NAME } from '@libs/utils';
import { fullSeriesTypes, mapImagesToFullSeries, mapMessageAttachmentsToImagingGalleryItems } from '@libs/imaging';
import { TranslateService } from '@ngx-translate/core';
import { CollaborationChatHeaderVM } from '../components/collaboration-chat-header/collaboration-chat-header.interface';

export const mapCollaborations = (
  collaborations: Collaboration[],
  userId?: string,
  isUserSiteAdmin?: boolean,
  translateService?: TranslateService,
): CollabSidebarElement[] => {
  return [...collaborations]
    .sort((a, b) => sortCollabsByLastInteractionDesc(a, b))
    .map((collaboration) => {
      const participants = mapParticipants(collaboration.participants);
      const isSupportCollab = collaboration.participants.employees.some((employee) => employee.pmsId === 'ffdsupport');

      // Find the sender of the last message
      const lastMessageSender = collaboration.lastMessage
        ? collaboration.participants.employees.find((employee) => employee.userId === collaboration.lastMessage?.senderId) ||
          collaboration.participants.patient
        : undefined;

      const isLastMessageFromCurrentUser = collaboration.lastMessage?.senderId === userId;

      return {
        id: collaboration.id,
        title: collaboration.title,
        linkedPatient: collaboration.subject,
        siteId: collaboration.siteId,
        hasUnreadMessage: collaboration.hasUnreadMessage,
        hasAttachmentInLastMessage: !!collaboration.lastMessage?.attachments?.length,
        participants,
        avatars: participants.length > 1 ? participants.filter((participant) => participant.userId !== userId) : participants,
        dateOfLastMessage: collaboration.lastMessage ? new Date(collaboration.lastMessage.sentAt) : undefined,
        lastMessage: collaboration.lastMessage?.text,
        lastMessageSenderFirstName: isLastMessageFromCurrentUser
          ? translateService?.instant('collab_last_message_sender_you')
          : lastMessageSender?.firstName,
        isCreatedBySelf: !!userId && userId === collaboration.createdByUserId,
        createdByUserId: collaboration.createdByUserId,
        isSupportCollab,
        daysLeftOfDeletion: calculateDaysLeftOfDeletion(collaboration.deletedAfter),
        isUserSiteAdmin,
      } satisfies CollabSidebarElement;
    });
};

export const calculateDaysLeftOfDeletion = (deletedAfter?: string): number | undefined => {
  if (!deletedAfter) return undefined;

  const hoursCountInADay = 24;

  const dayInMs = 1000 * 3600 * hoursCountInADay;

  const deletedAfterDate = new Date(deletedAfter);
  const deletedAfterDateMs = deletedAfterDate.getTime();

  const now = new Date();
  const nowMs = now.getTime();

  const daysLeft = Math.ceil((deletedAfterDateMs - nowMs) / dayInMs);

  return daysLeft > 0 ? daysLeft : 0;
};

export const mapParticipants = (participants: CollabParticipants): Person[] => {
  const providers = participants.employees.map((employee) => ({
    id: employee.id,
    userId: employee.userId,
    firstName: employee.firstName,
    lastName: employee.lastName,
    email: employee.pmsEmail,
  }));

  const patient = participants.patient
    ? {
        id: participants.patient.id,
        userId: participants.patient.userId,
        firstName: participants.patient.firstName,
        lastName: participants.patient.lastName,
      }
    : undefined;

  const emailParticipants = participants.emails?.map((email) => ({
    email,
  }));

  return [...providers, ...(patient ? [patient] : []), ...(emailParticipants ?? [])];
};

export const sortCollabsByLastInteractionDesc = (a: Collaboration, b: Collaboration): number => {
  const aDate = new Date(a.lastInteractionAt);
  const bDate = new Date(b.lastInteractionAt);

  return bDate.getTime() - aDate.getTime();
};

export const getSortedMessagesAscending = (messages: Message[]): Message[] => {
  return [...messages].sort((a, b) => new Date(a.sentAt).getTime() - new Date(b.sentAt).getTime());
};

export const mapMessages = (messages: CollaborationMessage[], selectedCollaboration?: Collaboration, userId?: string): MessageVM[] => {
  return [...messages].map((message) => {
    const sender =
      selectedCollaboration?.participants.employees.find((provider) => provider.userId === message.senderId) ||
      selectedCollaboration?.participants.patient;

    const allAttachments = message.attachments?.map((attachment) => {
      return {
        mediaType: attachment.mediaType,
        id: attachment.id,
        file: attachment.file,
        toothPositions: attachment.toothPositions,
        url: attachment.url,
        thumbnailUrl: attachment.thumbnailUrl,
        name: attachment.toothPositionString,
        date: attachment.date,
      };
    });

    const [periapicalsAndBitewings, others] = filterWithRemaining(
      message.attachments ?? [],
      (attachment) => fullSeriesTypes.includes(attachment.category) && !!attachment.toothPositions?.length,
    );

    const fullSeries =
      periapicalsAndBitewings.length > 0
        ? mapImagesToFullSeries(mapMessageAttachmentsToImagingGalleryItems(periapicalsAndBitewings))
        : undefined;

    return {
      id: message.id,
      text: message.text,
      sentAt: message.sentAt,
      sender: {
        firstName: sender?.firstName ?? UNKNOWN_PROVIDER_FIRST_NAME,
        lastName: sender?.lastName ?? UNKNOWN_PROVIDER_LAST_NAME,
        isSelf: message.senderId === userId,
      },
      attachments: {
        fullSeries,
        allAttachments: allAttachments ?? [],
        uncategorized: others.map((attachment) => {
          return {
            mediaType: attachment.mediaType,
            id: attachment.id,
            toothPositions: attachment.toothPositions,
            url: attachment.url,
            thumbnailUrl: attachment.thumbnailUrl,
            name: attachment.toothPositionString,
            date: attachment.date,
            file: attachment.file,
          };
        }),
      },
      isSending: message.isSending,
      isFailed: message.isFailed,
      isSystemMessage: message.isSystemMessage,
    };
  });
};

export const mapCollaborationToChatHeader = (
  selectedCollaboration?: Collaboration,
  selectedPatient?: PatientDetails,
  userId?: string,
): CollaborationChatHeaderVM | undefined => {
  if (!selectedCollaboration) {
    return;
  }
  const employeeParticipants: SelectedParticipant[] =
    selectedCollaboration?.participants.employees.map((employee) => ({
      id: employee.id,
      userId: employee.userId,
      firstName: employee.firstName,
      lastName: employee.lastName,
      email: employee.pmsEmail,
      status: employee.status,
      pmsProviderId: employee.pmsId,
      participantRole: employee.employeeType,
    })) ?? [];
  const emailParticipants: SelectedParticipant[] =
    selectedCollaboration?.participants.emails?.map((email) => ({
      id: '0',
      email,
      status: EmployeeStatus.PENDING,
      participantRole: EmployeeType.PROVIDER,
      firstName: email,
      lastName: '',
    })) ?? [];
  const patientParticipant: SelectedParticipant | null = selectedCollaboration?.participants.patient
    ? {
        id: selectedCollaboration.participants.patient.id,
        userId: selectedCollaboration.participants.patient.userId,
        firstName: selectedCollaboration.participants.patient.firstName,
        lastName: selectedCollaboration.participants.patient.lastName,
        status: selectedCollaboration.participants.patient.status,
        pmsPatientId: selectedCollaboration.participants.patient.pmsPatientId,
        participantRole: 'patient',
      }
    : null;

  const allParticipants = [...employeeParticipants, ...(patientParticipant ? [patientParticipant] : []), ...emailParticipants];

  return {
    subject: selectedCollaboration.title,
    employeeParticipants: employeeParticipants,
    patientParticipant,
    emailParticipants,
    allParticipants: allParticipants.length > 1 ? allParticipants.filter((participant) => participant.userId !== userId) : allParticipants,
    linkedPatientName: selectedCollaboration.subject
      ? `${selectedCollaboration.subject.firstName} ${selectedCollaboration.subject.lastName}`
      : undefined,
    linkedPatient: selectedCollaboration.subject ? { ...selectedCollaboration.subject, participantRole: 'patient' } : undefined,
    siteId: selectedCollaboration.siteId,
    selectedPatient: selectedPatient,
  };
};

export const isProvider = (item: unknown): item is Provider => {
  return (item as Provider).pmsId !== undefined;
};

export const isPatient = (item: unknown): item is Patient => {
  return (item as Patient).pmsPatientId !== undefined;
};
